import * as Sentry from "@sentry/react";
import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { useRef, useState } from "react";
import eyeClose from "../assets/img/eye-close.png";
import eyeOpen from "../assets/img/eye-open.png";
import { ReactComponent as Logo } from "assets/img/logo.svg";
import background from "assets/img/skyscraper-background.webp";
import { Button, Input, ValidationMessage } from "components/elements";
import { redirectManager } from "lib/redirectManager";
import { tokenManager } from "lib/tokenManager";
import { LoginUser, LoginUserSchema } from "@compass/shared/types";
import { API_BASE } from "../config";

type LoginSuccessResponse = { token: string };
type LoginTwoFactorResponse = { twoFactorRequired: true; tempToken: string };
// PRTL-1348: multi-org users pick an org before receiving a real token.
type LoginOrgSelectionResponse = {
    orgSelectionRequired: true;
    orgSelectToken: string;
    organizations: Array<{ id: number; name: string }>;
};
type LoginResponse = LoginSuccessResponse | LoginTwoFactorResponse | LoginOrgSelectionResponse;
type LoginErrorResponse = {
    message: string;
    /** PRTL-1388: the two-factor step is over; start again from the password. */
    restartLogin?: boolean;
};

export const Login = () => {
    const router = useRouter();
    const [hidePassword, setHidePassword] = useState(true);
    const [requires2FA, setRequires2FA] = useState(false);
    const [tempToken, setTempToken] = useState("");
    const [twoFactorCode, setTwoFactorCode] = useState("");
    const [twoFactorError, setTwoFactorError] = useState("");
    const [serverError, setServerError] = useState("");
    const [orgSelection, setOrgSelection] = useState<LoginOrgSelectionResponse | null>(null);
    const [orgError, setOrgError] = useState("");
    const [selectingOrgId, setSelectingOrgId] = useState<number | null>(null);
    const loginResultRef = useRef<LoginResponse | null>(null);

    const loginMutation = useMutation({
        mutationFn: async (value: { email: string; password: string }): Promise<LoginResponse> => {
            const response = await fetch(`${API_BASE}/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(value),
            });
            if (!response.ok) {
                const error: LoginErrorResponse = await response.json();
                throw new Error(error.message || "Invalid email or password");
            }
            return response.json();
        },
    });

    const form = useForm({
        defaultValues: {
            email: "",
            password: "",
        },
        validators: {
            onSubmit: LoginUserSchema,
            onSubmitAsync: async ({ value }) => {
                setServerError("");
                loginResultRef.current = null;
                try {
                    const result = await loginMutation.mutateAsync(value);
                    loginResultRef.current = result;
                    return undefined;
                } catch (e: any) {
                    const message = e.message || "Invalid email or password";
                    setServerError(message);
                    return message;
                }
            },
        },
        onSubmit: () => {
            const result = loginResultRef.current!;
            if ("twoFactorRequired" in result) {
                setTempToken(result.tempToken);
                setRequires2FA(true);
            } else if ("orgSelectionRequired" in result) {
                setOrgError("");
                setOrgSelection(result);
            } else {
                tokenManager.set(result.token);
                const savedPath = redirectManager.consume();
                router.navigate({ to: (savedPath || "/") as "/" });
            }
        },
    });

    const togglePassword = () => setHidePassword((prev) => !prev);

    const handle2FASubmit = async () => {
        try {
            const response = await fetch(`${API_BASE}/verify-2fa`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tempToken}`,
                },
                body: JSON.stringify({ code: twoFactorCode }),
            });
            if (!response.ok) {
                const error: LoginErrorResponse = await response.json();
                if (error.restartLogin) {
                    setRequires2FA(false);
                    setTempToken("");
                    setTwoFactorCode("");
                    setTwoFactorError("");
                    setServerError(error.message || "Please sign in again.");
                    return;
                }
                setTwoFactorError(error.message || "Invalid code");
                return;
            }
            const result: LoginSuccessResponse | LoginOrgSelectionResponse = await response.json();
            if ("orgSelectionRequired" in result) {
                setRequires2FA(false);
                setOrgError("");
                setOrgSelection(result);
                return;
            }
            tokenManager.set(result.token);
            location.reload();
        } catch (e) {
            console.error(e);
            setTwoFactorError("Unable to connect to the server. Please try again.");
        }
    };

    // PRTL-1348: exchange the org-select token for a token scoped to the
    // chosen organization.
    const handleSelectOrg = async (organizationId: number) => {
        if (!orgSelection || selectingOrgId !== null) return;
        setOrgError("");
        setSelectingOrgId(organizationId);
        try {
            const response = await fetch(`${API_BASE}/select-org`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    orgSelectToken: orgSelection.orgSelectToken,
                    organizationId,
                }),
            });
            if (!response.ok) {
                const error: LoginErrorResponse = await response.json();
                if (response.status === 401) {
                    // Org-select token expired — back to the login form, with
                    // the reason shown there instead of a stale picker error.
                    setOrgError("");
                    setServerError(
                        error.message || "Organization selection expired. Please log in again.",
                    );
                    setOrgSelection(null);
                    return;
                }
                setOrgError(error.message || "Unable to select that organization.");
                return;
            }
            const result: LoginSuccessResponse = await response.json();
            tokenManager.set(result.token);
            const savedPath = redirectManager.consume();
            router.navigate({ to: (savedPath || "/") as "/" });
        } catch (e) {
            Sentry.captureException(e, {
                extra: { action: "selectOrganization", organizationId },
            });
            setOrgError("Unable to connect to the server. Please try again.");
        } finally {
            setSelectingOrgId(null);
        }
    };

    return (
        <div className="flex min-h-screen w-full flex-row bg-gradient-to-br from-[#e8f4f8] to-[#d4e9f0] p-6">
            <div className="relative hidden w-[55%] items-end overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600 p-16 lg:flex">
                <div className="absolute inset-0 rounded-2xl">
                    <img
                        src={background}
                        alt="Modern buildings"
                        className="h-full w-full rounded-2xl object-cover opacity-60"
                    />
                </div>
                <div className="relative z-10 max-w-md text-white">
                    <h1 className="mb-4 text-4xl font-bold leading-tight">
                        Outsourced CFO &<br />
                        Accounting Solutions
                    </h1>
                    <p className="text-lg text-gray-200">For the Wealth Management Industry</p>
                </div>
            </div>
            <div className="flex w-full items-center justify-center lg:w-[40%]">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        form.handleSubmit();
                    }}
                    className="w-full max-w-md px-8"
                    method="POST"
                >
                    <div className="flex flex-col">
                        <div className="">
                            <Logo className="mx-auto mb-10 w-52" />
                            <h1 className="font-roboto mb-6 text-center text-[28px] font-bold leading-[42px] tracking-[0px] text-black">
                                Welcome to COMPASS
                            </h1>
                            <h2 className="font-roboto mb-4 text-[25px] font-bold leading-[44px] tracking-[0px] text-[#424242]">
                                {orgSelection ? "Choose an Organization" : "Log In to your Account"}
                            </h2>
                        </div>
                        {orgSelection ? (
                            <div className="mt-4">
                                <p className="mb-4 text-sm text-gray-600">
                                    Your account belongs to more than one organization. Pick the one
                                    you want to work in — you can switch at any time from the menu
                                    in the top right.
                                </p>
                                {orgError && (
                                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                        {orgError}
                                    </div>
                                )}
                                <ul className="flex flex-col gap-3">
                                    {orgSelection.organizations.map((org) => (
                                        <li key={org.id}>
                                            <button
                                                type="button"
                                                onClick={() => handleSelectOrg(org.id)}
                                                disabled={selectingOrgId !== null}
                                                className="flex w-full items-center gap-3 rounded-lg border border-[#738a8b] bg-transparent px-4 py-3 text-left transition hover:border-blue-500 hover:bg-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60"
                                            >
                                                <span className="bg-brand-atlantic flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white">
                                                    {org.name.charAt(0).toUpperCase()}
                                                </span>
                                                <span className="text-sm font-medium text-gray-900">
                                                    {org.name}
                                                </span>
                                                {selectingOrgId === org.id && (
                                                    <span className="ml-auto text-xs text-gray-500">
                                                        Signing in…
                                                    </span>
                                                )}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : !requires2FA ? (
                            <>
                                <form.Field name="email">
                                    {(field) => (
                                        <div className="relative mb-5 w-full">
                                            <label
                                                htmlFor="email"
                                                className="absolute -top-2.5 left-3 bg-gradient-to-br from-[#e8f4f8] to-[#d4e9f0] px-2 text-xs font-medium text-gray-600"
                                            >
                                                Email
                                            </label>
                                            <input
                                                id="email"
                                                name={field.name}
                                                type="email"
                                                autoComplete="username"
                                                value={field.state.value}
                                                onChange={(e) => {
                                                    field.handleChange(e.target.value);
                                                    setServerError("");
                                                }}
                                                onBlur={field.handleBlur}
                                                className="w-full rounded-lg border border-[#738a8b] bg-transparent px-4 py-3 text-sm text-gray-900 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-50"
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                    )}
                                </form.Field>
                                <form.Field name="password">
                                    {(field) => (
                                        <div className="relative mb-4 w-full">
                                            <label
                                                htmlFor="password"
                                                className="absolute -top-2.5 left-3 z-10 bg-gradient-to-br from-[#e8f4f8] to-[#d4e9f0] px-2 text-xs font-medium text-gray-600"
                                            >
                                                Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    name={field.name}
                                                    type={hidePassword ? "password" : "text"}
                                                    id="password"
                                                    autoComplete="current-password"
                                                    value={field.state.value}
                                                    onChange={(e) =>
                                                        field.handleChange(e.target.value)
                                                    }
                                                    onBlur={field.handleBlur}
                                                    className="w-full rounded-lg border border-[#738a8b] bg-transparent px-4 py-3 pr-12 text-sm text-gray-900 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-50"
                                                    placeholder="Enter password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={togglePassword}
                                                    className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-500 hover:text-gray-700"
                                                >
                                                    <img
                                                        src={hidePassword ? eyeClose : eyeOpen}
                                                        alt={hidePassword ? "Show" : "Hide"}
                                                        className="h-5 w-5"
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </form.Field>
                                <div className="mb-6 flex items-center justify-between text-sm">
                                    <label className="flex cursor-pointer items-center text-gray-700">
                                        <input
                                            type="checkbox"
                                            className="checked:bg-brand-atlantic mr-2 h-4 w-4 appearance-none rounded-sm border border-[#738a8b] bg-transparent text-blue-500 focus:ring-2 focus:ring-blue-500/20"
                                            disabled={loginMutation.isPending}
                                        />
                                        Remember me
                                    </label>
                                    <Link
                                        to="/forgot-password"
                                        className="font-medium text-[#424242] hover:!underline"
                                    >
                                        Forgot Password?
                                    </Link>
                                </div>
                                {serverError && (
                                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                        <ValidationMessage error={serverError} />
                                    </div>
                                )}
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="mb-8 h-14 w-full text-[13px] font-semibold tracking-wide"
                                    isLoading={loginMutation.isPending}
                                >
                                    CONTINUE
                                </Button>
                            </>
                        ) : (
                            <div className="mt-8">
                                {twoFactorError && (
                                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                        {twoFactorError}
                                    </div>
                                )}
                                <label
                                    htmlFor="2fa-code"
                                    className="mb-1 block text-sm text-gray-600"
                                >
                                    Enter 2FA Code
                                </label>
                                <Input
                                    id="2fa-code"
                                    value={twoFactorCode}
                                    onChange={(e) => setTwoFactorCode(e.target.value)}
                                    placeholder="123456"
                                />
                                <Button
                                    variant="primary"
                                    className="mt-4 w-full py-4 text-white"
                                    disabled={twoFactorCode.length !== 6}
                                    onClick={handle2FASubmit}
                                >
                                    Verify Code
                                </Button>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};
